/**
 * Pure geometry for the map primitive.
 *
 * Nothing here imports Leaflet: the itinerary maths is the part that has to be
 * verifiable, and a unit test should not need a DOM to check that a route was
 * cut at the right place.
 */

const EARTH_RADIUS_M = 6371008.8;

/** Accepts every point shape the API and the map produce. */
export function toLatLng(value) {
  if (!value) return null;
  if (Array.isArray(value)) {
    const [lat, lng] = value;
    return isValidLatLng(lat, lng) ? [Number(lat), Number(lng)] : null;
  }
  if (typeof value === 'object') {
    const lat = value.lat ?? value.latitude;
    const lng = value.lng ?? value.lon ?? value.longitude;
    return isValidLatLng(lat, lng) ? [Number(lat), Number(lng)] : null;
  }
  return null;
}

function isValidLatLng(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  return (
    Number.isFinite(a) &&
    Number.isFinite(b) &&
    Math.abs(a) <= 90 &&
    Math.abs(b) <= 180
  );
}

/** Great-circle distance in metres. */
export function distanceMeters(a, b) {
  const p1 = toLatLng(a);
  const p2 = toLatLng(b);
  if (!p1 || !p2) return Infinity;
  const rad = Math.PI / 180;
  const dLat = (p2[0] - p1[0]) * rad;
  const dLng = (p2[1] - p1[1]) * rad;
  const lat1 = p1[0] * rad;
  const lat2 = p2[0] * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * OSRM ships a route's shape as a Google-encoded polyline: ASCII chunks
 * holding successive coordinate deltas. Forty lines of bit shifting are
 * cheaper than a dependency whose only job is this.
 */
export function decodePolyline(value, precision = 5) {
  if (typeof value !== 'string' || value.length === 0) return [];
  const factor = 10 ** precision;
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  const readValue = () => {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      if (index >= value.length) return null;
      byte = value.charCodeAt(index) - 63;
      index += 1;
      if (!Number.isFinite(byte) || byte < 0) return null;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };

  while (index < value.length) {
    const dLat = readValue();
    if (dLat === null) break;
    const dLng = readValue();
    if (dLng === null) break;
    lat += dLat;
    lng += dLng;
    points.push([lat / factor, lng / factor]);
  }

  return points;
}

/**
 * `route_geometry_json` is produced by another service and is not shipped yet.
 * Every plausible shape is accepted and anything else yields an empty route:
 * a missing or malformed itinerary must draw nothing, never throw.
 */
export function parseRouteGeometry(value) {
  if (value == null) return [];

  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('{') || text.startsWith('[')) {
      try {
        return parseRouteGeometry(JSON.parse(text));
      } catch {
        return [];
      }
    }
    return sanitize(decodePolyline(text));
  }

  if (Array.isArray(value)) return sanitize(value.map(toLatLng));

  if (typeof value === 'object') {
    if (typeof value.encoded === 'string') {
      return sanitize(
        decodePolyline(
          value.encoded,
          Number.isFinite(value.precision) ? value.precision : 5,
        ),
      );
    }
    if (typeof value.polyline === 'string') return parseRouteGeometry(value.polyline);
    if (value.type === 'Feature') return parseRouteGeometry(value.geometry);
    if (value.geometry) return parseRouteGeometry(value.geometry);
    // GeoJSON is longitude-first; the rest of the app is latitude-first.
    if (value.type === 'LineString' && Array.isArray(value.coordinates)) {
      return sanitize(value.coordinates.map((c) => toLatLng([c?.[1], c?.[0]])));
    }
    if (Array.isArray(value.coordinates)) return parseRouteGeometry(value.coordinates);
    if (Array.isArray(value.points)) return parseRouteGeometry(value.points);
  }

  return [];
}

const sanitize = (points) => (points || []).filter(Boolean).map(toLatLng).filter(Boolean);

/** Index of the route vertex closest to `position`, or -1. */
export function nearestIndex(points, position) {
  const here = toLatLng(position);
  if (!here || !Array.isArray(points) || points.length === 0) return -1;
  let best = -1;
  let bestDistance = Infinity;
  points.forEach((point, index) => {
    const d = distanceMeters(point, here);
    if (d < bestDistance) {
      bestDistance = d;
      best = index;
    }
  });
  return best;
}

/**
 * A truck that left the planned road must not drag the itinerary after it, so
 * the live position is only welded into the drawing when it is genuinely on
 * the route. Beyond that the two halves still split at the nearest vertex —
 * the driver is late on the corridor, not somewhere else entirely.
 */
export const ON_ROUTE_TOLERANCE_M = 2000;

/**
 * Distance d'un point au tracé lui-même, et non à son sommet le plus proche.
 *
 * La nuance décide de tout : un tracé d'itinéraire n'a de sommet que là où la
 * route tourne. Sur une ligne droite du corridor Dakar–Kayes, deux sommets
 * consécutifs peuvent être séparés de dix kilomètres. Mesurée au sommet, une
 * zone posée exactement sur la route, à mi-chemin entre deux d'entre eux,
 * paraît à cinq kilomètres de côté — et se fait classer « hors corridor »
 * alors que le camion la traverse.
 *
 * La projection est planaire : à l'échelle d'un segment routier, l'écart avec
 * la géodésique est négligeable devant la tolérance de deux kilomètres.
 */
export function projectOnRoute(points, position) {
  const route = sanitize(points);
  const here = toLatLng(position);
  if (!here || route.length === 0) {
    return { offsetMeters: Infinity, alongMeters: null };
  }
  if (route.length === 1) {
    return { offsetMeters: distanceMeters(route[0], here), alongMeters: 0 };
  }

  const latitudeScale = Math.cos((here[0] * Math.PI) / 180) || 1;
  const planar = ([lat, lon]) => [lat, lon * latitudeScale];
  const target = planar(here);

  let best = { offsetMeters: Infinity, alongMeters: null };
  let travelled = 0;

  for (let i = 0; i < route.length - 1; i += 1) {
    const a = planar(route[i]);
    const b = planar(route[i + 1]);
    const dLat = b[0] - a[0];
    const dLon = b[1] - a[1];
    const lengthSquared = dLat * dLat + dLon * dLon;
    const segmentMeters = distanceMeters(route[i], route[i + 1]);

    let t = 0;
    if (lengthSquared > 0) {
      t = ((target[0] - a[0]) * dLat + (target[1] - a[1]) * dLon) / lengthSquared;
      // Hors du segment, le point le plus proche est l'une de ses extrémités.
      t = Math.max(0, Math.min(1, t));
    }
    const projectedLat = route[i][0] + (route[i + 1][0] - route[i][0]) * t;
    const projectedLon = route[i][1] + (route[i + 1][1] - route[i][1]) * t;
    const offset = distanceMeters([projectedLat, projectedLon], here);
    if (offset < best.offsetMeters) {
      best = { offsetMeters: offset, alongMeters: travelled + segmentMeters * t };
    }
    travelled += segmentMeters;
  }
  return best;
}

/** Écart latéral au tracé, en mètres. */
export function distanceToRouteMeters(points, position) {
  return projectOnRoute(points, position).offsetMeters;
}

/** Au-delà de cet écart au tracé prévu, la trace est surlignée « hors itinéraire ». */
export const HORS_ITINERAIRE_M = 5000;

/**
 * Portions de la trace roulées à plus de `seuil` mètres de l'itinéraire prévu,
 * chacune prolongée d'un point de part et d'autre pour se raccorder à la trace.
 * Même seuil d'ouverture que l'alerte du serveur (ROUTE_DEVIATION_OPEN_METERS).
 */
export function portionsHorsItineraire(route, positions, seuil = HORS_ITINERAIRE_M) {
  const trace = Array.isArray(positions) ? positions : [];
  if (sanitize(route).length < 2 || trace.length < 2) return [];
  const loin = trace.map((p) => {
    const ecart = distanceToRouteMeters(route, p);
    return Number.isFinite(ecart) && ecart > seuil;
  });
  const portions = [];
  let debut = -1;
  for (let i = 0; i <= trace.length; i += 1) {
    if (i < trace.length && loin[i]) {
      if (debut < 0) debut = i;
    } else if (debut >= 0) {
      portions.push(trace.slice(Math.max(0, debut - 1), Math.min(trace.length, i + 1)));
      debut = -1;
    }
  }
  return portions.filter((portion) => portion.length > 1);
}

export function splitRouteAtPosition(points, position) {
  const route = sanitize(points);
  if (route.length < 2) return { travelled: [], remaining: route };

  const here = toLatLng(position);
  if (!here) return { travelled: [], remaining: route };

  const index = nearestIndex(route, here);
  if (index < 0) return { travelled: [], remaining: route };

  const onRoute = distanceMeters(route[index], here) <= ON_ROUTE_TOLERANCE_M;
  // The joint vertex belongs to both halves, otherwise the two polylines show
  // a gap exactly where the eye is looking.
  const travelled = route.slice(0, index + 1);
  const remaining = route.slice(index);
  if (onRoute) {
    return { travelled: [...travelled, here], remaining: [here, ...remaining] };
  }
  return { travelled, remaining };
}

/**
 * Une zone du corridor se lit « déjà derrière » ou « encore devant ». Celle qui
 * n'est nulle part près de l'itinéraire ne reçoit aucun verdict plutôt qu'un
 * faux.
 *
 * L'ordre se mesure en **mètres parcourus le long du tracé**, et non par index
 * de sommet. Un itinéraire n'a de sommet que là où la route tourne : sur une
 * ligne droite, deux zones distantes de vingt kilomètres partageraient le même
 * sommet le plus proche, et leur ordre relatif deviendrait arbitraire.
 */
export function classifyCorridorZones(zones, routePoints, position) {
  const route = sanitize(routePoints);
  const list = Array.isArray(zones) ? zones : [];
  if (route.length < 2) return list.map((zone) => ({ ...zone, corridor: null }));

  const here = projectOnRoute(route, position);
  const hereAlong = here.offsetMeters === Infinity ? null : here.alongMeters;

  return list.map((zone) => {
    const center = toLatLng([zone.latitude, zone.longitude]);
    if (!center) return { ...zone, corridor: null };
    const projection = projectOnRoute(route, center);
    const reach = Number(zone.radius_meters || 0) + ON_ROUTE_TOLERANCE_M;
    if (projection.offsetMeters > reach) return { ...zone, corridor: null };
    if (hereAlong === null) return { ...zone, corridor: 'upcoming' };
    return {
      ...zone,
      corridor: projection.alongMeters <= hereAlong ? 'passed' : 'upcoming',
    };
  });
}

/** Metric readout in the product's locale; `null` when there is nothing to say. */
export function formatDistance(meters) {
  // `Number(null)` vaut 0, et `Number('')` aussi. Sans ce filtre, une distance
  // inconnue s'affichait « 0 m » — c'est-à-dire « vous êtes arrivé » — au lieu
  // de ne rien dire. Un chiffre faux sur une distance restante est pire qu'une
  // absence de chiffre.
  if (meters === null || meters === undefined || meters === '') return null;
  const value = Number(meters);
  if (!Number.isFinite(value) || value < 0) return null;
  if (value < 1000) return `${Math.round(value)} m`;
  const km = value / 1000;
  return `${km.toLocaleString('fr-FR', {
    maximumFractionDigits: km < 100 ? 1 : 0,
  })} km`;
}

/** Total length of a decoded route, used when the backend sends no distance. */
export function routeLengthMeters(points) {
  const route = sanitize(points);
  let total = 0;
  for (let i = 1; i < route.length; i += 1) {
    total += distanceMeters(route[i - 1], route[i]);
  }
  return total;
}

/** "12.5, -1.5" typed into the zone search box is a location, not a name. */
export function parseCoordinateInput(text) {
  if (typeof text !== 'string') return null;
  const match = text
    .trim()
    .match(/^(-?\d{1,3}(?:[.,]\d+)?)\s*[,;/\s]\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
  if (!match) return null;
  return toLatLng([
    Number(match[1].replace(',', '.')),
    Number(match[2].replace(',', '.')),
  ]);
}
